import { parse, stringify } from 'yaml';

import { DATAMAPPER_PREFIX } from './mapping-steps';

/** One kind of bill with a mapping of its own, such as `SR` → "Return". */
export interface SplitBranch {
  value: string;
  name: string;
}

export interface SplitSpec {
  /** Dotted, with `[]` for the first entry of a list: `invoiceType`, `orderItems[].status`. */
  path: string;
  branches: SplitBranch[];
  /** What the bills that match no branch are called; they keep the endpoint's current mapping. */
  otherwise: string;
}

type Node = Record<string, unknown>;

const isNode = (value: unknown): value is Node => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const slug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** The header `platform-field` reads a path into: its last name, as the data plane names it. */
export const fieldHeader = (path: string): string =>
  path.slice(path.lastIndexOf('.') + 1).replace(/\[\d*]/g, '') || 'field';

/** Every field a set of bills has, as paths a split can name. A list's entries are read from its first. */
export const samplePaths = (payloads: unknown[]): string[] => {
  const paths = new Set<string>();
  const visit = (value: unknown, path: string) => {
    if (Array.isArray(value)) {
      if (value.length) visit(value[0], `${path}[]`);
      return;
    }
    if (isNode(value)) {
      Object.entries(value).forEach(([name, child]) => {
        visit(child, path ? `${path}.${name}` : name);
      });
      return;
    }
    if (path) paths.add(path);
  };
  payloads.forEach((payload) => {
    visit(Array.isArray(payload) ? payload[0] : payload, '');
  });
  return [...paths].sort();
};

/** What a path reads in one bill, the way the data plane's `platform-field` reads it. */
export const readPath = (payload: unknown, path: string): string | undefined => {
  let node: unknown = Array.isArray(payload) ? payload[0] : payload;
  for (const part of path.split('.')) {
    const name = part.replace(/\[\d*]$/, '');
    if (name) node = isNode(node) ? node[name] : undefined;
    const index = part.match(/\[(\d*)]$/);
    if (index && Array.isArray(node)) node = node[index[1] ? Number(index[1]) : 0];
  }
  if (node === undefined || node === null) return undefined;
  return typeof node === 'object' ? JSON.stringify(node) : String(node);
};

/** The values a field takes across the samples, most seen first. */
export const sampleValues = (payloads: unknown[], path: string): { value: string; count: number }[] => {
  const counts = new Map<string, number>();
  payloads.forEach((payload) => {
    const value = readPath(payload, path);
    if (value !== undefined) counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  return [...counts.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
};

/** Where the rules one kind of bill is held to live in a draft, such as `contract.return.json`. */
export const kindRulesResource = (kind: string): string => `contract.${kind}.json`;

/** The kinds of bill a flow checks against rules of their own, by the name each check gives. */
export const contractKinds = (flow: string | undefined): string[] => {
  if (!flow?.trim()) return [];
  let document: unknown;
  try {
    document = parse(flow);
  } catch {
    return [];
  }
  const kinds: string[] = [];
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) node.forEach(visit);
    if (!isNode(node)) return;
    const to = node.to;
    if (isNode(to) && String(to.uri ?? '').startsWith('platform-contract') && isNode(to.parameters)) {
      const kind = String(to.parameters.rules ?? '').trim();
      if (kind && !kinds.includes(kind)) kinds.push(kind);
    }
    Object.values(node).forEach(visit);
  };
  visit(document);
  return kinds;
};

/** Which bills each kind's rules are for: the field the door reads and the value that picks the kind. */
export const kindSelectors = (flow: string | undefined): { kind: string; path: string; value: string }[] => {
  if (!flow?.trim()) return [];
  let document: unknown;
  try {
    document = parse(flow);
  } catch {
    return [];
  }
  const found: { kind: string; path: string; value: string }[] = [];
  let path = '';
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) node.forEach(visit);
    if (!isNode(node)) return;
    const uri = isNode(node.to) ? String(node.to.uri ?? '') : '';
    if (uri.startsWith('platform-field:')) path = uri.slice('platform-field:'.length).split('?')[0];
    const when = isNode(node.expression) && isNode(node.expression.simple) ? node.expression.simple.expression : '';
    const value = typeof when === 'string' ? when.match(/==\s*'([^']*)'/)?.[1] : undefined;
    if (value !== undefined && path) {
      const kinds = contractKinds(stringify(node.steps ?? []));
      kinds.forEach((kind) => found.push({ kind, path, value }));
    }
    Object.values(node).forEach(visit);
  };
  visit(document);
  return found;
};

/** The name a branch's rules and mapping go by: `Return` is `return`. */
export const kindOf = (name: string): string => slug(name);

/** Why a split cannot be drawn, in words, or nothing when it can. */
export const splitFault = (spec: SplitSpec): string | undefined => {
  if (!/^[A-Za-z_]\w*(\[\d*])?(\.[A-Za-z_]\w*(\[\d*])?)*$/.test(spec.path)) return 'Choose a field from the bill.';
  if (spec.branches.length === 0) return 'Give at least one value its own mapping.';
  const values = spec.branches.map((branch) => branch.value.trim());
  const names = [...spec.branches.map((branch) => branch.name.trim()), spec.otherwise.trim()];
  if (values.some((value) => !value || /['"\\]/.test(value)))
    return 'Each value needs to be written out, without quotes or backslashes.';
  if (new Set(values).size !== values.length) return 'Each value can have only one mapping.';
  if (names.some((name) => !slug(name))) return 'Give every branch a name.';
  if (new Set(names.map(slug)).size !== names.length) return 'Give every branch a different name.';
  return undefined;
};

/**
 * The flow with its mapping split by one field of the bill: the field is read, each listed value
 * gets a DataMapper step of its own, and every other bill keeps the mapping the endpoint already
 * has. It is an ordinary Camel choice afterwards, edited on the canvas like any other.
 */
const valueCheck = (header: string, value: string) => ({
  simple: { expression: `\${header.${header}} == '${value.trim()}'` },
});

/** A worker branch: the bill mapped by a step of its own, then checked like the main mapping. */
const workerBranch = (main: Node, checks: unknown[], header: string, { value, name }: SplitBranch) => {
  const id = `${String(main.id)}-${slug(name)}`;
  return {
    expression: valueCheck(header, value),
    description: name.trim(),
    steps: [
      {
        step: {
          id,
          description: `Map a ${name.trim()} bill to Casa's format`,
          steps: [
            { to: { uri: `xslt-saxon:${id}.xsl`, parameters: { useJsonBody: true } } },
            ...structuredClone(checks),
          ],
        },
      },
    ],
  };
};

/** A door branch: the bill held to its own kind's rules while the vendor waits. */
const doorBranch = (header: string, { value, name }: SplitBranch) => ({
  expression: valueCheck(header, value),
  description: name.trim(),
  steps: [
    {
      to: {
        uri: 'platform-contract',
        parameters: { rules: slug(name) },
        description: `Check a ${name.trim()} bill against its own rules`,
      },
    },
  ],
});

const routesOf = (document: unknown) =>
  (Array.isArray(document) ? document : []).map((entry) => (isNode(entry) ? entry.route : undefined));

const stepsFrom = (routes: unknown[], scheme: string): unknown[] => {
  const route = routes.find(
    (item) => isNode(item) && isNode(item.from) && String(item.from.uri ?? '').startsWith(scheme),
  ) as Node | undefined;
  return isNode(route?.from) && Array.isArray(route.from.steps) ? (route.from.steps as unknown[]) : [];
};

const choiceIn = (steps: unknown[]): Node | undefined => {
  const at = steps.findIndex(
    (item) => isNode(item) && isNode(item.to) && String(item.to.uri ?? '').startsWith('platform-field:'),
  );
  const next = at >= 0 ? steps[at + 1] : undefined;
  return isNode(next) && isNode(next.choice) ? next.choice : undefined;
};

const whenValue = (when: unknown): string | undefined => {
  const expression =
    isNode(when) && isNode(when.expression) && isNode(when.expression.simple)
      ? when.expression.simple.expression
      : undefined;
  return typeof expression === 'string' ? expression.match(/==\s*'([^']*)'/)?.[1] : undefined;
};

/** The split a flow already has, read back from its worker route, so it can be changed. */
export const readSplit = (flow: string | undefined): SplitSpec | undefined => {
  if (!flow?.trim()) return undefined;
  let routes: unknown[];
  try {
    routes = routesOf(parse(flow));
  } catch {
    return undefined;
  }
  const steps = stepsFrom(routes, 'kafka');
  const field = steps.find(
    (item) => isNode(item) && isNode(item.to) && String(item.to.uri ?? '').startsWith('platform-field:'),
  ) as Node | undefined;
  const choice = choiceIn(steps);
  if (!field || !choice || !Array.isArray(choice.when)) return undefined;
  const path = String((field.to as Node).uri)
    .slice('platform-field:'.length)
    .split('?')[0];
  return {
    path,
    branches: choice.when.flatMap((when) => {
      const value = whenValue(when);
      return value === undefined ? [] : [{ value, name: String((when as Node).description ?? value) }];
    }),
    otherwise: String((isNode(choice.otherwise) && choice.otherwise.description) || 'Other'),
  };
};

/**
 * Changes a split already on the flow: a branch kept by its value keeps its mapping and rules and
 * only takes the new name; a new value gets a mapping step and rules of its own; a value left out
 * loses its branch. The field itself is fixed: splitting by another field starts again.
 */
export const changeSplit = (flow: string, next: SplitSpec): string => {
  const current = readSplit(flow);
  if (!current) throw new Error('This flow is not split yet.');
  if (current.path !== next.path) throw new Error(`This flow is split by ${current.path}; that field stays.`);
  const fault = splitFault(next);
  if (fault) throw new Error(fault);
  const document = parse(flow) as unknown;
  const routes = routesOf(document);
  const header = fieldHeader(next.path);

  const worker = choiceIn(stepsFrom(routes, 'kafka'))!;
  const otherwise = isNode(worker.otherwise) ? worker.otherwise : {};
  const main = (
    (Array.isArray(otherwise.steps) ? otherwise.steps : []).find((item) => isNode(item) && isNode(item.step)) as
      | Node
      | undefined
  )?.step as Node | undefined;
  if (!main) throw new Error('The bills that match no value have lost their mapping step.');
  const checks = (Array.isArray(main.steps) ? main.steps : []).filter(
    (item) => !(isNode(item) && isNode(item.to) && String(item.to.uri ?? '').startsWith('xslt-saxon')),
  );
  const rebuild = (choice: Node, fresh: (branch: SplitBranch) => unknown) => {
    const held = new Map(
      (Array.isArray(choice.when) ? choice.when : []).map((when) => [whenValue(when), when as Node]),
    );
    choice.when = next.branches.map((branch) => {
      const kept = held.get(branch.value.trim());
      return kept ? { ...kept, description: branch.name.trim() } : fresh(branch);
    });
  };
  rebuild(worker, (branch) => workerBranch(main, checks, header, branch));
  worker.otherwise = { ...otherwise, description: next.otherwise.trim() };
  const door = choiceIn(stepsFrom(routes, 'platform-http'));
  if (door) rebuild(door, (branch) => doorBranch(header, branch));
  return stringify(document);
};

export const splitByField = (flow: string, spec: SplitSpec): string => {
  const fault = splitFault(spec);
  if (fault) throw new Error(fault);
  const document = parse(flow) as unknown;
  const routes = routesOf(document);
  const steps = stepsFrom(routes, 'kafka');
  const at = steps.findIndex(
    (item) => isNode(item) && isNode(item.step) && String(item.step.id ?? '').startsWith(DATAMAPPER_PREFIX),
  );
  if (at < 0)
    throw new Error(
      steps.some((item) => isNode(item) && 'choice' in item)
        ? 'This flow is already split. Change its branches on the canvas.'
        : 'This flow has no mapping step to split.',
    );

  const main = (steps[at] as Node).step as Node;
  const checks = (Array.isArray(main.steps) ? main.steps : []).filter(
    (item) => !(isNode(item) && isNode(item.to) && String(item.to.uri ?? '').startsWith('xslt-saxon')),
  );
  const header = fieldHeader(spec.path);

  /*
   * The same split at the door, so each kind is held to its own rules while the vendor waits and
   * a bad bill is refused before it is accepted. Every other bill keeps the checks it had.
   */
  const door = stepsFrom(routes, 'platform-http');
  const queue = door.findIndex(
    (item) => isNode(item) && isNode(item.to) && String(item.to.uri ?? '').startsWith('kafka'),
  );
  door.splice(
    queue < 0 ? door.length : queue,
    0,
    { to: { uri: `platform-field:${spec.path}`, description: `Read ${spec.path}` } },
    {
      choice: {
        description: `Each kind of bill checked against its own rules`,
        when: spec.branches.map((branch) => doorBranch(header, branch)),
      },
    },
  );

  steps.splice(
    at,
    1,
    { to: { uri: `platform-field:${spec.path}`, description: `Read ${spec.path}` } },
    {
      choice: {
        description: `Each kind of bill by ${spec.path}`,
        when: spec.branches.map((branch) => workerBranch(main, checks, header, branch)),
        otherwise: { description: spec.otherwise.trim(), steps: [steps[at]] },
      },
    },
  );
  return stringify(document);
};
